import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import { Box } from "@mui/material";
import { type FC, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useNotificationsContext } from "../../contexts/NotificationsContext";
import { useLatestRef } from "../../hooks/useLatestRef";
import { EmptyState } from "../UIToolkit/molecules/EmptyState";
import { NotificationItem } from "./NotificationItem";
import { NotificationSkeleton } from "./NotificationSkeleton";

/**
 * Where the list is shown — the `surface` on its mark-seen events. Each surface tracks its own view event (the page its
 * long-standing `notifications_page_viewed`, the bell tab `bell_panel_notifications_viewed`), so the page's reports keep
 * working.
 */
export type NotificationsListSurface = "page" | "bell_panel";

/** One placeholder card per key while the list loads. */
const SKELETON_KEYS = ["skeleton-1", "skeleton-2", "skeleton-3", "skeleton-4", "skeleton-5"] as const;

type Props = {
	surface: NotificationsListSurface;
	dataTestId: string;
};

/**
 * The signed-in user's notifications, newest first — the inbox the `/notifications` page shows and the admin bell's
 * "Notifications" tab reuses. Showing it marks every unseen notification seen (the bell's count drops), as opening the
 * page always has. Loading draws card placeholders; nothing to show is an "all set" state. A failed fetch is reported
 * by `NotificationsProvider` (a snackbar), which keeps the last list.
 *
 * The caller owns the frame (background, padding, scrolling); this is just the column of cards.
 */
export const NotificationsList: FC<Props> = ({ surface, dataTestId }) => {
	const { t } = useTranslation();
	const { track, trackError } = useAnalytics();
	const { allNotifications, markNotificationsSeen, markNotificationClicked, isLoading } = useNotificationsContext();
	const trackRef = useLatestRef(track);
	const trackErrorRef = useLatestRef(trackError);
	// The provider re-creates `markNotificationsSeen` on every render: read it through a ref so a re-render while a
	// request is in flight doesn't send the same ids again.
	const markSeenRef = useLatestRef(markNotificationsSeen);
	const markedIds = useRef(new Set<number>());

	const sorted = useMemo(() => [...allNotifications].sort((a, b) => b.created_at.valueOf() - a.created_at.valueOf()), [allNotifications]);

	// Seeing the list is seeing them: mark the unseen ones, so the bell's count matches what is left to read.
	useEffect(() => {
		const unseenIds = allNotifications
			.filter((notification) => !notification.isSeen && !markedIds.current.has(notification.id))
			.map((notification) => notification.id);
		if (unseenIds.length === 0) return;
		for (const id of unseenIds) markedIds.current.add(id);
		let isMounted = true;
		markSeenRef
			.current(unseenIds)
			.then((success) => {
				if (!success) {
					// The provider already told the reader; let a later render try these ids again.
					for (const id of unseenIds) markedIds.current.delete(id);
					return;
				}
				if (isMounted) trackRef.current("notifications_marked_seen_on_page_load", { surface, count: unseenIds.length });
			})
			.catch((error: unknown) => {
				for (const id of unseenIds) markedIds.current.delete(id);
				trackErrorRef.current("notifications_mark_seen_failed", {
					surface,
					count: unseenIds.length,
					error_message: error instanceof Error ? error.message : String(error),
				});
			});
		return () => {
			isMounted = false;
		};
	}, [allNotifications, surface, trackRef, trackErrorRef, markSeenRef]);

	if (isLoading) {
		return (
			<Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }} data-testid={`${dataTestId}-loading`}>
				{SKELETON_KEYS.map((key) => (
					<NotificationSkeleton key={key} />
				))}
			</Box>
		);
	}

	if (sorted.length === 0) {
		return (
			<Box sx={{ display: "flex", flex: 1, alignItems: "center", justifyContent: "center", width: "100%" }}>
				<EmptyState icon={<NotificationsNoneIcon />} title={t("you.are.all.set")} hint={t("nothing.new.here")} dataTestId={`${dataTestId}-empty`} />
			</Box>
		);
	}

	return (
		<Box component="ul" sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%", m: 0, p: 0, listStyle: "none" }} data-testid={dataTestId}>
			{sorted.map((notification) => (
				<Box component="li" key={notification.id} data-testid={`notification-item-${notification.id}`} sx={{ width: "100%" }}>
					<NotificationItem notification={notification} markNotificationClicked={markNotificationClicked} />
				</Box>
			))}
		</Box>
	);
};
